import { orderBy } from 'lodash';
import { CATEGORY_TYPE } from '@fiverr-private/listing_lib';
import { ALL_SUBCATEGORY } from '../../../../../../../lib/utils/side_filters/constants';
import { OPTION_PILLS_COUNT } from '../FilterRow.constants';
import type { ExposedFiltersType } from '../../../side_filters/types';
import type { CategoryExposure, CategoryOption, SubCategoryFilter, VisualFiltersData } from '../FilterRow.types';

interface ResolveCategoryExposureParams {
    subCategory?: SubCategoryFilter;
    dominantLeafCategory?: string;
    dominantLeafCategoryParent?: string;
    exposedFilters?: ExposedFiltersType;
    visualFilters?: VisualFiltersData;
}

// The filters the backend exposes for the dominant leaf category, in the order the old exposed row showed them.
const EXPOSED_CATEGORY_SPECIFIC_KEYS = ['visual_filters', 'metadata_filters'] as const;

const buildCategorySpecific = (
    exposedFilters?: ExposedFiltersType,
    visualFilters?: VisualFiltersData
): CategoryExposure => {
    const filterId = [
        visualFilters?.visualFiltersList?.[0]?.id,
        ...EXPOSED_CATEGORY_SPECIFIC_KEYS.map((key) => exposedFilters?.[key]?.[0]?.id),
    ].find(Boolean);

    return filterId ? { mode: 'categorySpecific', filterId } : { mode: 'categorySpecific' };
};

const toCategoryOption = (child: Omit<CategoryOption, 'id'>): CategoryOption => ({
    ...child,
    id: child.params.nested_sub_category || child.params.sub_category || ALL_SUBCATEGORY,
});

const toAllCategoriesOption = ({ alias, count, selected, params }: SubCategoryFilter): CategoryOption => ({
    id: ALL_SUBCATEGORY,
    alias,
    count,
    selected,
    params,
});

/**
 * Decides which category controls the row exposes for a search query:
 * - "categories": the Categories dropdown, plus the top NSC pills. Used when the query has no dominant
 *   leaf category (DLC), or when the DLC is a sub category that has NSCs with results. With a DLC the
 *   dropdown lists only those NSCs, otherwise every related category.
 * - "categorySpecific": the first category specific dropdown (style, logo type...), plus its top values.
 *   Used when the DLC is an NSC, or a sub category without NSCs with results. That dropdown is the first
 *   Visual filter, else the first visual or metadata filter the backend exposes.
 *
 * The backend sends the DLC and its parent: both are the sub category id for a sub category DLC, and the NSC id
 * and its parent sub category id for an NSC DLC.
 */
export const resolveCategoryExposure = ({
    subCategory,
    dominantLeafCategory,
    dominantLeafCategoryParent,
    exposedFilters,
    visualFilters,
}: ResolveCategoryExposureParams): CategoryExposure => {
    const categorySpecific = buildCategorySpecific(exposedFilters, visualFilters);
    const children = (subCategory?.children ?? []).filter(({ count }) => !!count);

    if (!subCategory || children.length === 0) {
        return categorySpecific;
    }

    const dominantLeaf = String(dominantLeafCategory ?? '');
    const dominantParent = String(dominantLeafCategoryParent ?? '');

    // An NSC DLC arrives with its parent sub category id, a sub category DLC with its own id.
    if (dominantLeaf && dominantLeaf !== dominantParent) {
        return categorySpecific;
    }

    const options = children.map(toCategoryOption);
    const nscOptions = options.filter(({ type }) => type === CATEGORY_TYPE.NESTED_SUB_CATEGORY);
    // Without a DLC any NSC can be offered; with one, only the NSCs of its own sub category, already merged on the server.
    const dominantNscOptions = (subCategory.nestedSubCategories ?? [])
        .filter(({ count }) => !!count)
        .map(toCategoryOption);
    const pillCandidates = dominantLeaf ? dominantNscOptions : nscOptions;

    if (dominantLeaf && pillCandidates.length === 0) {
        return categorySpecific;
    }

    return {
        mode: 'categories',
        options: [toAllCategoriesOption(subCategory), ...(dominantLeaf ? dominantNscOptions : options)],
        topOptions: orderBy(pillCandidates, ({ count }) => count ?? 0, 'desc').slice(0, OPTION_PILLS_COUNT),
    };
};
